import { useState, useEffect, useCallback, useRef } from 'react';
import { Stack, Title, Group, Button, Text, Box, Card, Skeleton, Anchor, Divider, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconKey } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTeamMember } from '../../hooks/useTeam';
import { useCreateUser, useUpdateUser, useCheckUsernameAvailable } from '../../hooks/useTeamMutations';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAccountPermissions } from '../../useAccountPermissions';
import { TeamMemberFormFields } from './TeamMemberForm';
import { PermissionControls } from './PermissionControls';
import { PasswordResetModal } from './PasswordResetModal';
import { StreamPickerModal } from './StreamPickerModal';
import {
  teamMemberCreateSchema,
  teamMemberEditSchema,
  type TeamMemberCreateFormValues,
  type TeamMemberEditFormValues,
} from '../../schemas/teamSchemas';
import type { CPUserPermissionViewData, CPUserMediaData } from '@/lib/types/Auth';

const DEFAULT_PERMISSIONS: CPUserPermissionViewData = {
  api: false,
  accountNotify: false,
  support: false,
  billingNotify: false,
  billingRead: false,
  billingWrite: false,
  userRead: false,
  userWrite: false,
  serviceRead: false,
  serviceWrite: false,
  subcustomerRead: false,
  subcustomerWrite: false,
  ingest: false,
  ingestcfgId: null,
  roleBroadcaster: false,
};

export function TeamMemberPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const rawId = (params as Record<string, string | undefined>).id;
  const isCreate = !rawId || rawId === 'new';
  const cpuserId = isCreate ? null : Number(rawId);

  const { user, isLoading, isError } = useTeamMember(cpuserId);
  const { authData, isSubaccount } = useAuth();
  const { canViewUploadProfiles } = useAccountPermissions();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const checkUsername = useCheckUsernameAvailable();

  const currentIsAdmin = authData?.cpuser.core.isAdmin ?? false;
  const isTargetAdmin = user?.core.isAdmin ?? false;
  const canEditPermissions = !isTargetAdmin || currentIsAdmin;

  // Permissions state (managed outside RHF for coupling logic)
  const [permissions, setPermissions] = useState<CPUserPermissionViewData>(DEFAULT_PERMISSIONS);
  const [cpuserMedias, setCpuserMedias] = useState<CPUserMediaData[]>([]);
  const [showStreamPicker, setShowStreamPicker] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Baseline for dirty-checking permissions/medias (set when user data loads)
  const initialPermissionsRef = useRef<CPUserPermissionViewData | null>(null);
  const initialMediasRef = useRef<CPUserMediaData[] | null>(null);

  // Username availability check
  const [usernameStatus, setUsernameStatus] = useState<'checking' | 'available' | 'taken' | null>(null);
  const usernameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create form
  const createForm = useForm<TeamMemberCreateFormValues>({
    resolver: zodResolver(teamMemberCreateSchema),
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      ingestcfgId: null,
    },
  });

  // Edit form
  const editForm = useForm<TeamMemberEditFormValues>({
    resolver: zodResolver(teamMemberEditSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      ingestcfgId: null,
    },
  });

  // Populate edit form when user data loads
  useEffect(() => {
    if (user && !isCreate) {
      editForm.reset({
        firstName: user.core.firstName,
        lastName: user.core.lastName,
        email: user.core.email,
        phone: user.core.phone,
        ingestcfgId: user.permissions.ingestcfgId,
      });
      setPermissions(user.permissions);
      setCpuserMedias(user.cpuserMedias);
      initialPermissionsRef.current = user.permissions;
      initialMediasRef.current = user.cpuserMedias;
    }
  }, [user, isCreate, editForm]);

  // Debounced username availability check
  const watchedUsername = createForm.watch('username');
  useEffect(() => {
    if (!isCreate) return;
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);

    if (!watchedUsername || watchedUsername.length < 3) {
      setUsernameStatus(null);
      return;
    }

    setUsernameStatus('checking');
    usernameTimerRef.current = setTimeout(async () => {
      try {
        await checkUsername.mutateAsync(watchedUsername);
        setUsernameStatus('available');
      } catch {
        setUsernameStatus('taken');
      }
    }, 300);

    return () => {
      if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    };
  }, [watchedUsername, isCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePermissionsChange = useCallback((newPerms: CPUserPermissionViewData) => {
    setPermissions(newPerms);
  }, []);

  async function handleCreateSubmit(values: TeamMemberCreateFormValues) {
    if (usernameStatus === 'taken') {
      notifications.show({ title: 'Error', message: 'Username is not available.', color: 'red' });
      return;
    }

    try {
      await createMutation.mutateAsync({
        email: values.email.toLowerCase(),
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        username: values.username.toLowerCase(),
        password: values.password,
        permissions: { ...permissions, ingestcfgId: values.ingestcfgId },
        cpuserMedias,
      });
      notifications.show({ title: 'Created', message: 'Team member added.', color: 'green' });
      navigate({ to: '/account/team' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create user.',
        color: 'red',
      });
    }
  }

  async function handleEditSubmit(values: TeamMemberEditFormValues) {
    if (!cpuserId) return;

    try {
      await updateMutation.mutateAsync({
        cpuserId,
        req: {
          email: values.email.toLowerCase(),
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          permissions: { ...permissions, ingestcfgId: values.ingestcfgId },
          cpuserMedias,
        },
      });
      notifications.show({ title: 'Saved', message: 'Team member updated.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update user.',
        color: 'red',
      });
    }
  }

  if (!isCreate && isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={200} />
        <Skeleton height={200} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!isCreate && isError) {
    return (
      <Stack gap="lg">
        <Anchor component="button" onClick={() => navigate({ to: '/account/team' })}>
          <Group gap={4}><IconArrowLeft size={16} /> Back to Team</Group>
        </Anchor>
        <Alert color="red">Failed to load user. Please try again later.</Alert>
      </Stack>
    );
  }

  if (!isCreate && !user) {
    return (
      <Stack gap="lg">
        <Anchor component="button" onClick={() => navigate({ to: '/account/team' })}>
          <Group gap={4}><IconArrowLeft size={16} /> Back to Team</Group>
        </Anchor>
        <Alert color="red">User not found.</Alert>
      </Stack>
    );
  }

  const isPending = isCreate ? createMutation.isPending : updateMutation.isPending;
  const formDirty = isCreate ? createForm.formState.isDirty : editForm.formState.isDirty;
  const permsDirty = !isCreate && initialPermissionsRef.current != null
    && JSON.stringify(permissions) !== JSON.stringify(initialPermissionsRef.current);
  const mediasDirty = !isCreate && initialMediasRef.current != null
    && JSON.stringify(cpuserMedias.map(m => m.mediaId).sort()) !== JSON.stringify(initialMediasRef.current.map(m => m.mediaId).sort());
  const isDirty = formDirty || permsDirty || mediasDirty;
  const pageTitle = isCreate ? 'Add Team Member' : `${user!.core.firstName} ${user!.core.lastName}`;

  return (
    <Stack gap="lg">
      <Anchor component="button" onClick={() => navigate({ to: '/account/team' })} size="sm">
        <Group gap={4}><IconArrowLeft size={16} /> Back to Team</Group>
      </Anchor>

      <Group justify="space-between">
        <Title order={2}>{pageTitle}</Title>
        {!isCreate && (
          <Button
            variant="light"
            leftSection={<IconKey size={16} />}
            onClick={() => setShowPasswordReset(true)}
          >
            Reset Password
          </Button>
        )}
      </Group>

      {!isCreate && user?.core.username && (
        <Text size="sm" c="dimmed" ff="monospace">Username: {user.core.username}</Text>
      )}

      <Box
        pos="sticky"
        top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
        w="fit-content"
        ml="auto"
        style={{ zIndex: 10 }}
      >
        <Button
          type="submit"
          form="team-member-form"
          loading={isPending}
          disabled={!isCreate && !isDirty}
        >
          {isCreate ? 'Create User' : 'Save Changes'}
        </Button>
      </Box>

      <form
        id="team-member-form"
        onSubmit={isCreate
          ? createForm.handleSubmit(handleCreateSubmit)
          : editForm.handleSubmit(handleEditSubmit)
        }
      >
        <Stack gap="lg">
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            {isCreate ? (
              <TeamMemberFormFields
                control={createForm.control}
                isCreate
                usernameStatus={usernameStatus}
              />
            ) : (
              <TeamMemberFormFields
                control={editForm.control}
                isCreate={false}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">Permissions</Text>
            {!canEditPermissions && (
              <Alert color="yellow" mb="md">
                Admin permissions cannot be modified.
              </Alert>
            )}
            <PermissionControls
              permissions={permissions}
              onChange={handlePermissionsChange}
              cpuserMedias={cpuserMedias}
              onMediasChange={setCpuserMedias}
              onSelectStreams={() => setShowStreamPicker(true)}
              disabled={!canEditPermissions}
              isSubaccount={isSubaccount}
              showUploadProfile={canViewUploadProfiles}
            />
          </Card>
        </Stack>
      </form>

      <StreamPickerModal
        opened={showStreamPicker}
        onClose={() => setShowStreamPicker(false)}
        cpuserId={cpuserId ?? 0}
        selectedMediaIds={cpuserMedias.map(m => m.mediaId)}
        onSave={setCpuserMedias}
      />

      {!isCreate && user && (
        <PasswordResetModal
          user={showPasswordReset ? user : null}
          onClose={() => setShowPasswordReset(false)}
        />
      )}
    </Stack>
  );
}
